import React, { useCallback, useRef } from 'react';
import { ConversationProvider } from '@elevenlabs/react';

import AiDocumentUploadDialog from './AiDocumentUploadDialog';
import AiCompanionConversation from './AiCompanionConversation';
import OnboardingRecapDialog from './OnboardingRecapDialog';
import { AiCompanionSessionContext } from './types';
import useAiCompanionController from '../../../hooks/AiOnboarding/useAiCompanionController';

interface IAiCompanion {
  sessionContext: AiCompanionSessionContext;
  signupRequestId: string;
  onBack: () => void;
  onFallbackToManual: () => void;
  onSwitchToManual: () => void;
}

const AiCompanion = ({
  sessionContext,
  signupRequestId,
  onBack,
  onFallbackToManual,
  onSwitchToManual,
}: IAiCompanion) => {
  const conversationIdRef = useRef<string | null>(null);
  const getConversationId = useCallback(
    () =>
      conversationIdRef.current,
    [],
  );
  const handleConversationConnected = useCallback(
    (conversationId: string) => {
      conversationIdRef.current = conversationId;
    },
    [],
  );
  const controller = useAiCompanionController({
    getConversationId,
    sessionContext,
    signupRequestId,
    switchToManualMode: onSwitchToManual,
  });
  const isDialogOpen = controller.documentUploadDialogProps.isOpen
    || controller.isRecapOpen;
  const isInteractionPaused = isDialogOpen
    || controller.isClientToolProcessing;
  const shouldShowClientToolProcessing = controller.isClientToolProcessing
    && !isDialogOpen;

  return (
    <>
      <ConversationProvider
        clientTools={controller.clientTools}
        isMuted={!controller.isVoiceMode}
        onMutedChange={controller.handleProviderMutedChange}
      >
        <AiCompanionConversation
          error={controller.visibleError}
          getDynamicVariables={controller.getDynamicVariables}
          isClientToolProcessing={shouldShowClientToolProcessing}
          isInteractionPaused={isInteractionPaused}
          isVoiceMode={controller.isVoiceMode}
          onBack={onBack}
          onConversationConnected={handleConversationConnected}
          onErrorChange={controller.setError}
          onFallbackToManual={onFallbackToManual}
          onSwitchToManual={onSwitchToManual}
          onVoiceModeChange={controller.setIsVoiceMode}
          signupRequestId={signupRequestId}
        />
      </ConversationProvider>
      <AiDocumentUploadDialog
        documentType={controller.documentUploadDialogProps.documentType}
        hasUploadError={controller.documentUploadDialogProps.hasUploadError}
        isOpen={controller.documentUploadDialogProps.isOpen}
        isUploading={controller.documentUploadDialogProps.isUploading}
        onClose={controller.documentUploadDialogProps.onClose}
        onUpload={controller.documentUploadDialogProps.onUpload}
        requestId={controller.documentUploadDialogProps.requestId}
      />
      <OnboardingRecapDialog
        open={controller.isRecapOpen}
        sessionContext={sessionContext}
        snapshot={controller.snapshot}
        onConfirm={controller.handleConfirmRecap}
      />
    </>
  );
};

export default AiCompanion;
